import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Card, cn, Spinner } from '@design-system';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';

import {
  AlertTriangle,
  Brain,
  Calendar,
  ChartNoAxesCombined,
  CheckCircle2,
  FileText,
  Home,
  Lock,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

import { useWizard } from 'react-use-wizard';

import { formatCurrency } from '@shared';
import { analytics } from '@shared/posthog';
import { selectClient } from '@client/model/selectors';

import {
  DEMENTIA_MEMORY_CARE_YEARS,
  useTypeCFeasibility,
  type TypeCFeasibility,
  type TypeCFeasibilityStatus,
  type TypeCScenario,
} from '@/features/ccrc-type-c';
import type { ReactNode } from 'react';

const compactYears = (years: number) => `${years.toFixed(1)}y`;
const longYears = (years: number) => `${years.toFixed(1)} years`;

/** Qualitative position of an event within a care phase (0 = start, 1 = end). */
const phasePosition = (fraction: number): string => {
  if (fraction < 1 / 3) return 'early in';
  if (fraction < 2 / 3) return 'partway through';
  return 'late in';
};

interface ScenarioTheme {
  card: string;
  iconWrap: string;
  iconColor: string;
  divider: string;
  barShades: string[];
  footerText: string;
}

const OK_THEME: ScenarioTheme = {
  card: 'border-green-200 bg-green-50',
  iconWrap: 'bg-green-100',
  iconColor: 'text-green-800',
  divider: 'border-green-200',
  barShades: ['bg-green-300', 'bg-green-500', 'bg-green-700'],
  footerText: 'text-green-700',
};

const RISK_THEME: ScenarioTheme = {
  card: 'border-red-200 bg-red-50',
  iconWrap: 'bg-red-100',
  iconColor: 'text-red-700',
  divider: 'border-red-200',
  barShades: ['bg-orange-300', 'bg-orange-500', 'bg-red-600'],
  footerText: 'text-red-700',
};

const STATUS_BADGE: Record<
  TypeCFeasibilityStatus,
  { label: string; variant: 'success' | 'destructive'; icon: ReactNode }
> = {
  'feasible': {
    label: 'Feasible',
    variant: 'success',
    icon: <CheckCircle2 className="size-3.5" />,
  },
  'close': {
    label: 'Close to Feasible',
    variant: 'success',
    icon: <CheckCircle2 className="size-3.5" />,
  },
  'at-risk': {
    label: 'At Risk',
    variant: 'destructive',
    icon: <AlertTriangle className="size-3.5" />,
  },
};

interface ScenarioCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  scenario: TypeCScenario;
  scaleYears: number;
  extendBarToFraction?: number;
}

function ScenarioCard({
  title,
  description,
  icon: Icon,
  scenario,
  scaleYears,
  extendBarToFraction,
}: ScenarioCardProps) {
  const theme = scenario.status === 'at-risk' ? RISK_THEME : OK_THEME;
  const badge = STATUS_BADGE[scenario.status];
  const isSurplus = scenario.fundingResult >= 0;
  const resultText = `${isSurplus ? '+' : '−'}${formatCurrency(Math.abs(scenario.fundingResult))}`;

  const axisPct = (years: number) => `${(years / scaleYears) * 100}%`;
  const runOutLeft = scenario.fundsRunOutYear !== null ? axisPct(scenario.fundsRunOutYear) : null;

  const phasesEndFraction = scenario.totalCareYears / scaleYears;
  const extensionWidth =
    extendBarToFraction != null ? Math.max(0, extendBarToFraction - phasesEndFraction) : 0;

  return (
    <Card className={cn('mb-4 gap-0 p-6', theme.card)}>
      <div className="grid items-start gap-4 md:grid-cols-[auto_1fr_auto_auto_auto]">
        <div
          className={cn('flex size-14 items-center justify-center rounded-full', theme.iconWrap)}
        >
          <Icon className={cn('size-6', theme.iconColor)} />
        </div>
        <div>
          <p className="font-semibold text-gray-950">{title}</p>
          <p className="text-foreground mt-1 text-sm">{description}</p>
        </div>
        <div className={cn('h-full md:border-l md:pl-6', theme.divider)}>
          <p className="text-foreground text-sm">Projected LTC Cost</p>
          <p className="font-sans text-xl font-bold text-gray-950">
            {formatCurrency(scenario.projectedLtcCost)}
          </p>
        </div>
        <div className={cn('h-full md:border-l md:pl-6', theme.divider)}>
          <p className="text-foreground text-sm">Funding Result</p>
          <p
            className={cn('font-sans text-xl font-bold text-red-700', {
              'text-green-700': isSurplus,
            })}
          >
            {resultText}
          </p>
          <p className="text-foreground text-xs">{isSurplus ? 'Surplus' : 'Shortfall'}</p>
        </div>
        <div className={cn('h-full md:border-l md:pl-6', theme.divider)}>
          <p className="text-foreground mb-1 text-sm">Status</p>
          <Badge
            variant={badge.variant}
            className="rounded-full px-3 py-1"
          >
            {badge.icon}
            {badge.label}
          </Badge>
        </div>
      </div>

      <div className={cn('mt-5 border-t pt-4', theme.divider)}>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-foreground text-sm font-medium">Care Phase Progression</span>
          <span className="text-foreground text-sm font-medium">
            {longYears(scenario.totalCareYears)} total
          </span>
        </div>
        <div className="relative pt-5">
          {runOutLeft !== null && scenario.fundsRunOutYear !== null && (
            <div
              className="pointer-events-none absolute top-0 -translate-x-1/2"
              style={{ left: runOutLeft }}
            >
              <div className="flex flex-col items-center">
                <span className="whitespace-nowrap rounded bg-red-900 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  Funds out · {compactYears(scenario.fundsRunOutYear)}
                </span>
                <div className="h-2 w-0.5 bg-red-900" />
              </div>
            </div>
          )}
          <div className="relative flex h-3 w-full overflow-hidden rounded-full bg-white">
            {scenario.phases.map((phase, index) => (
              <div
                key={phase.label}
                className={theme.barShades[index] ?? theme.barShades[theme.barShades.length - 1]}
                style={{ width: axisPct(phase.years) }}
              />
            ))}
            {extensionWidth > 0 && (
              <div
                className={theme.barShades[theme.barShades.length - 1]}
                style={{ width: `${extensionWidth * 100}%` }}
              />
            )}
            {runOutLeft !== null && (
              <div
                className="pointer-events-none absolute top-0 h-3 w-0.5 -translate-x-1/2 bg-red-900"
                style={{ left: runOutLeft }}
              />
            )}
          </div>
          <div className="text-foreground mt-2 flex text-xs">
            {scenario.phases.map(phase => (
              <div
                key={phase.label}
                style={{ width: axisPct(phase.years) }}
              >
                <span className="font-medium">{phase.label}</span> · {compactYears(phase.years)}
              </div>
            ))}
          </div>
        </div>
        {scenario.fundsRunOutYear === null ? (
          <p className={`mt-3 flex items-center gap-1.5 text-xs font-medium ${theme.footerText}`}>
            <CheckCircle2 className="size-3.5" />
            Funding covers the full projected care duration.
          </p>
        ) : (
          <p className={`mt-3 flex items-center gap-1.5 text-xs font-medium ${theme.footerText}`}>
            <AlertTriangle className="size-3.5" />
            Funding runs out at {longYears(scenario.fundsRunOutYear)}
            {scenario.fundsRunOutPhaseLabel !== null &&
              scenario.fundsRunOutPhaseFraction !== null &&
              `, ${phasePosition(scenario.fundsRunOutPhaseFraction)} the ${scenario.fundsRunOutPhaseLabel} phase`}
            .
          </p>
        )}
      </div>
    </Card>
  );
}

function feasibilitySummary(feasibility: TypeCFeasibility): { headline: string; lines: string[] } {
  const expectedOk = feasibility.expected.status !== 'at-risk';
  const dementiaOk = feasibility.dementia.status !== 'at-risk';

  if (expectedOk && dementiaOk) {
    return {
      headline: 'This resident can self-fund care under both scenarios.',
      lines: [
        'Expected care is covered by projected assets.',
        'Even a dementia event with extended memory care stays within reach.',
      ],
    };
  }

  if (expectedOk && !dementiaOk) {
    return {
      headline: 'Expected care is within reach, but dementia creates a funding risk.',
      lines: [
        'Under expected care assumptions, this resident has enough projected assets.',
        'A dementia event requiring extended memory care would lead to a shortfall.',
      ],
    };
  }

  return {
    headline: 'This resident faces a funding shortfall.',
    lines: [
      'Projected assets fall short of the expected cost of care.',
      'A dementia event would widen the gap further.',
    ],
  };
}

function FeasibilityContent({ feasibility }: { feasibility: TypeCFeasibility }) {
  const { resident, selfFundingCapacity, yearsTillLtc, expected, dementia } = feasibility;
  const summary = feasibilitySummary(feasibility);

  const scaleYears = Math.max(expected.totalCareYears, dementia.totalCareYears, 1);
  const fundsOutFraction =
    dementia.fundsRunOutYear !== null ? dementia.fundsRunOutYear / scaleYears : undefined;

  const dementiaMemoryCarePhase = dementia.phases[dementia.phases.length - 1];
  const dementiaMemoryCareYears = dementiaMemoryCarePhase?.years ?? DEMENTIA_MEMORY_CARE_YEARS;
  const dementiaDescription =
    dementiaMemoryCareYears > DEMENTIA_MEMORY_CARE_YEARS
      ? `Models a dementia event with a ${dementiaMemoryCareYears.toFixed(1)}-year memory care phase, reflecting this resident's longer projected full-care need.`
      : `Models a dementia event with a ${DEMENTIA_MEMORY_CARE_YEARS}-year memory care phase — the median memory care duration for people living with dementia.`;

  const profileLabel =
    resident.age !== null && resident.gender
      ? `${resident.age}-year-old ${resident.gender.toLowerCase()}`
      : 'Resident';

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-3 grid gap-4 md:grid-cols-2">
        <Card className="flex-row items-start gap-4 p-5">
          <div className="bg-primary/10 flex size-20 shrink-0 items-center justify-center rounded-md">
            <Wallet className="text-primary size-8" />
          </div>
          <div>
            <p className="text-foreground text-sm">Self-Funding Capacity</p>
            <p className="text-primary font-sans text-2xl font-bold">
              {formatCurrency(selfFundingCapacity)}
            </p>
            <p className="text-foreground mt-1 text-sm">
              {yearsTillLtc}-year projection based on your assets
            </p>
          </div>
        </Card>
        <Card className="flex-row items-center gap-4 p-5">
          <div className="bg-primary/10 flex size-20 shrink-0 items-center justify-center rounded-md">
            <Calendar className="text-primary size-8" />
          </div>
          <div>
            <p className="text-foreground text-sm">Resident Profile</p>
            <p className="font-sans text-2xl font-bold text-gray-950">{profileLabel}</p>
            {resident.stateAbbreviation && (
              <p className="text-foreground mt-1 text-sm">{resident.stateAbbreviation}</p>
            )}
          </div>
        </Card>
      </div>

      <h2 className="mb-4 mt-8 text-lg font-bold text-gray-950">Scenario Readiness</h2>

      <ScenarioCard
        title="Expected Care Scenario"
        description="Based on current care assumptions and typical progression."
        icon={Home}
        scenario={expected}
        scaleYears={scaleYears}
        extendBarToFraction={fundsOutFraction}
      />
      <ScenarioCard
        title="Dementia Scenario"
        description={dementiaDescription}
        icon={Brain}
        scenario={dementia}
        scaleYears={scaleYears}
      />

      <Card className="bg-primary/5 border-primary/10 p-6">
        <div className="flex items-start gap-4">
          <div className="bg-primary flex size-12 shrink-0 items-center justify-center rounded-full">
            <FileText className="size-6 text-white" />
          </div>
          <div>
            <p className="font-semibold text-gray-950">Feasibility Summary</p>
            <p className="font-semibold text-gray-950">{summary.headline}</p>
            {summary.lines.map(line => (
              <p
                key={line}
                className="text-foreground mt-2 text-sm"
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      </Card>

      <p className="text-foreground mt-6 flex items-center justify-center gap-1.5 text-center text-sm">
        <Lock className="size-3.5" />
        All projections are estimates only and not guarantees.
      </p>
    </div>
  );
}

export function CcrcTypeCFeasibilityStep() {
  const { nextStep, previousStep } = useWizard();
  const client = useSelector(selectClient);
  const feasibility = useTypeCFeasibility();

  // Track page view on mount
  useEffect(() => {
    if (client) {
      analytics.ccrc.typeCFeasibilityViewed({
        clientId: client.clientId,
        ccrcType: 'type-c',
        stepId: 'ccrc-type-c-feasibility',
      });
    }
  }, [client?.clientId]);

  return (
    <StepLayout
      title="Financial Feasibility"
      subtitle="Assess whether this resident can self-fund care under expected and dementia scenarios."
      icon={
        <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full">
          <ChartNoAxesCombined className="text-primary size-7" />
        </div>
      }
      nextButtonLabel="Continue"
      onNext={() => nextStep()}
      onBack={previousStep}
    >
      {feasibility ? (
        <FeasibilityContent feasibility={feasibility} />
      ) : (
        <div className="py-16">
          <Spinner className="text-primary mx-auto size-8" />
        </div>
      )}
    </StepLayout>
  );
}
